import { useRef, useState } from 'react'

import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'
import { FormikErrors } from 'formik'
import {
  ContactFragment,
  FinanceDealsSelectorFragment,
  LegalEntityForSelectFragment,
  PreviewQuoteDocument,
  QuoteFragment,
  QuoteUpsertPageDocument,
  SalesDocumentTemplateForSelectFragment,
  TaxRateForSelectFragment,
  UpsertFinanceQuoteFinanceLineItemsInput,
  UpsertQuoteDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'
import { debounce } from 'lodash'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import CurrencySelector from 'modules/finance/components/CurrencySelector'
import { FinanceDealsSelector } from 'modules/finance/components/Deals/FinanceDealsSelector'
import DocumentViewer from 'modules/finance/components/DocumentViewer'
import { adjustLineItemsTaxRateBasedOnCountry } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm'
import { CustomizeSection } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/_CustomizeSection'
import { DiscountInput } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/_DiscountInput'
import { LineItemsSectionWithModal } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/_LineItemsSectionWithModal'
import { SectionTitle } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/_SectionTitle'
import { LineItem } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/types'
import { getDefaultSalesDocumentTemplate } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/defaultValuesUtils'
import { fetchLogoFile } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/formUtils'
import { FinanceSalesDocumentTemplateSelector } from 'modules/finance/components/SalesDocumentTemplates/FinanceSalesDocumentTemplateSelector'
import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

const extractDateValue = (date: string | undefined) =>
  date !== undefined && date !== '' ? dateBackendToUI(date) : ''

const mapLineItemsForPreview = (lineItems: LineItem[]) => {
  return lineItems
    .map((item) => {
      return {
        description: item.description,
        quantity: item.quantity,
        amountCents: item.amount && toCents(item.amount),
        taxRateId: item.taxRate?.id,
      }
    })
    .filter((item) => item != null)
}

const checkLineItemErrors = (lineItems: LineItem[]) => {
  const lineItemsErrors: FormikErrors<LineItem>[] = lineItems.map((item) => {
    const itemErrors: FormikErrors<LineItem> = {}

    if (!item.description) {
      itemErrors.description = i18n.t('finance.sale_invoices.form.error_required')
    }
    if (!item.quantity || item.quantity == 0) {
      itemErrors.quantity = i18n.t('finance.sale_invoices.form.error_one_or_higher')
    }
    if (!item.amount || item.amount < 0) {
      itemErrors.amount = i18n.t('finance.sale_invoices.form.error_required')
    }
    if (!item.taxRate) {
      itemErrors.taxRate = i18n.t('finance.sale_invoices.form.error_required')
    }
    return itemErrors
  })
  if (lineItemsErrors?.every((lineItemsError) => Object.keys(lineItemsError).length == 0)) {
    return []
  }
  return lineItemsErrors
}

interface QuoteUpsertSidePanelProps {
  quoteId?: string
  onClose: () => void
  defaultLegalEntity?: LegalEntityForSelectFragment | null
  defaultCurrency?: string
  defaultDate: string
  documentToRefetchOnCreate: DocumentNode
  defaultDealId?: number
}

const UpsertSidePanel = ({
  quoteId,
  defaultDealId,
  onClose,
  defaultLegalEntity,
  defaultCurrency,
  defaultDate,
  documentToRefetchOnCreate,
}: QuoteUpsertSidePanelProps) => {
  const { company, access } = useCurrentSession()

  const [previewHtml, setPreviewHtml] = useState<string | undefined>()

  const [upsertQuote, {}] = useMutation(UpsertQuoteDocument, {
    extractorFn: (data) => data.financeMutations.upsertFinanceQuote,
    refetchQueries: [documentToRefetchOnCreate],
    awaitRefetchQueries: true,
    update(cache) {
      cache.evict({
        id: quoteId,
        fieldName: 'FinanceQuote',
      })
    },
  })

  const [previewQuote, {}] = useMutation(PreviewQuoteDocument, {
    extractorFn: (data) => data.financeMutations.previewFinanceQuotePreview,
  })

  const { data: quoteData, error } = useQuery(QuoteUpsertPageDocument, {
    variables: {
      id: quoteId ? Number(quoteId) : -1,
      quotePresent: !!quoteId,
      country: defaultLegalEntity?.country,
      dealIdPresent: !!defaultDealId,
      dealIds: defaultDealId ? [defaultDealId] : undefined,
    },
  })
  const quote = quoteData?.finance?.quote
  const taxRatesCounter: number = quoteData?.finance.taxRatesConnection.totalCount ?? 0
  const defaultTaxRate: TaxRateForSelectFragment | undefined =
    taxRatesCounter == 1 ? quoteData?.finance.taxRatesConnection.nodes[0] : undefined
  const canSeeDeals = quoteData?.finance._policies.sidebarSeeFinanceDealsSubLevelItem.can
  const defaultDeal = quoteData?.finance.dealsConnection?.nodes?.[0]
  const defaultClient = defaultDeal?.client
  const defaultSalesDocumentTemplates = quoteData?.finance.defaultSalesDocumentTemplates ?? []
  const salesDocumentTemplatesEnabled =
    quoteData?.finance._policies.salesDocumentTemplatesManage.can ?? false

  interface QuoteFormFields {
    currency?: string
    quoteNumber?: string
    expirationDate?: string
    documentDate?: string
    legalEntity?: LegalEntityForSelectFragment | null //issuer
    contact?: ContactFragment | null //receiver / recipient
    deal?: FinanceDealsSelectorFragment | null
    discountPercentage?: number | null
    lineItems: LineItem[]
    memo?: string
    footer?: string
    showMemo: boolean
    showFooter: boolean
    salesDocumentTemplate?: SalesDocumentTemplateForSelectFragment | null
    logoFile?: File | null
  }

  useUnsafeEffect(() => {
    if (defaultDeal) {
      form.setValues({ ...form.values, deal: defaultDeal, contact: defaultClient })
    }
  }, [defaultDeal])

  const onSubmit = async (values: QuoteFormFields) => {
    if (
      !values.currency ||
      !values.quoteNumber ||
      !values.legalEntity ||
      !values.contact ||
      !values.documentDate ||
      checkLineItemErrors(values.lineItems).length > 0
    ) {
      return { success: false, data: {}, errors: [] }
    }

    const result = await upsertQuote({
      variables: {
        id: quoteId ? Number(quoteId) : undefined,
        dealId: defaultDeal ? defaultDeal.id : values.deal?.id,
        currency: values.currency,
        quoteNumber: values.quoteNumber,
        expirationDate: values.expirationDate,
        documentDate: values.documentDate,
        legalEntityId: values.legalEntity?.id,
        contactId: defaultClient ? defaultClient.id : values.contact?.id,
        lineItems: mapLineItemsForUpsert(values.lineItems),
        discountPercentage: values.discountPercentage,
        memo: values.showMemo ? values.memo : null,
        footer: values.showFooter ? values.footer : null,
        logoFile: values.logoFile,
      },
    })

    const handled = handleResult(result, (errors) => {
      form.setErrors({ ...errors })
    })
    if (handled.success) {
      if (quoteId) {
        track(trackingEvents.accounting.quotes.submitEdit)
      } else {
        track(trackingEvents.accounting.quotes.submitCreate)
      }
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.quotes.notifications.saved_successfully'),
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
    return handled
  }

  const validateForm = (data: QuoteFormFields) => {
    const errors: FormErrors<QuoteFormFields> = {}

    if (!data.quoteNumber) {
      errors.quoteNumber = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.documentDate) {
      errors.documentDate = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.legalEntity) {
      errors.legalEntity = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.contact) {
      errors.contact = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!data.currency) {
      errors.currency = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (data.discountPercentage && (data.discountPercentage < 0 || data.discountPercentage > 100)) {
      errors.discountPercentage = i18n.t(
        'finance.sale_invoices.form.discount_less_than_or_equal_100'
      )
    }

    const lineItemsErrors = checkLineItemErrors(data.lineItems)

    if (lineItemsErrors.length > 0) {
      errors.lineItems = lineItemsErrors
    }

    return errors
  }

  const defaultQuoteLineItems = (quoteFragment?: QuoteFragment | null): LineItem[] => {
    return quoteFragment
      ? quoteFragment.lineItems.map((item) => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          amount: item.amountCents / 100,
          taxRate: {
            id: item.taxRateId,
            description: undefined,
            rate: item.taxRatePercentage,
            taxType: {
              name: item.taxTypeName ?? '',
              countryCode: item.taxTypeCountry,
            },
          },
        }))
      : []
  }

  const defaultLineItems = useUnsafeMemo(() => {
    return defaultQuoteLineItems(quote)
  }, [quote])

  const defaultSalesDocumentTemplate = getDefaultSalesDocumentTemplate(
    defaultLegalEntity,
    defaultSalesDocumentTemplates
  )
  const footer = !quote
    ? defaultSalesDocumentTemplate?.footer || undefined
    : quote?.footer || undefined

  const form = useForm<QuoteFormFields>({
    enableReinitialize: true,
    defaultValues: {
      currency: quote?.currency || defaultCurrency,
      quoteNumber: quote?.quoteNumber || undefined,
      documentDate: quote?.documentDate || defaultDate,
      expirationDate: quote?.expirationDate || undefined,
      legalEntity: quote?.legalEntity || defaultLegalEntity,
      contact: defaultClient || quote?.client || undefined,
      deal: defaultDeal || quote?.deal || undefined,
      lineItems: defaultLineItems,
      discountPercentage: quote?.discountPercentage || undefined,
      memo: quote?.memo || undefined,
      footer: footer,
      showMemo: !!quote?.memo,
      showFooter: !!footer,
      salesDocumentTemplate: !quote ? defaultSalesDocumentTemplate : undefined,
    },
    validate: validateForm,
    validateOnChange: false,
    onSubmit: async (data) => {
      return await onSubmit(data)
    },
  })

  const mapLineItemsForUpsert = (
    lineItems: LineItem[]
  ): UpsertFinanceQuoteFinanceLineItemsInput[] => {
    return lineItems
      .map((item) => {
        if (!item.quantity || !item.amount || !item.description || !item.taxRate?.id) {
          form.errors.lineItems = checkLineItemErrors(lineItems)
          return null
        }
        return {
          id: typeof item.id === 'number' ? item.id : undefined,
          type: 'simple' as const,
          description: item.description,
          quantityType: 'units' as const,
          quantity: item.quantity,
          unitPriceCents: toCents(item.amount),
          taxRateId: item.taxRate.id,
        }
      })
      .filter((item) => item != null)
  }

  const watcher = async (values: QuoteFormFields) => {
    const variables = {
      currency: values.currency,
      quoteNumber: values.quoteNumber,
      expirationDate: values.expirationDate,
      documentDate: values.documentDate,
      legalEntityId: values.legalEntity?.id,
      clientId: defaultClient ? defaultClient.id : values.contact?.id,
      dealId: defaultDeal ? defaultDeal.id : values.deal?.id,
      lineItems: mapLineItemsForPreview(values.lineItems),
      discountPercentage: values.discountPercentage,
      memo: values.showMemo ? values.memo : null,
      footer: values.showFooter ? values.footer : null,
      logoFile: values.logoFile,
    }

    const result = await previewQuote({
      variables: variables,
    })

    const handled = handleResult(result, handleResultWithFlash())
    if (handled.success) {
      setPreviewHtml(handled.data.quotePreview?.htmlContent)
    }
  }

  const debouncedGeneratePreviewRef = useRef(
    debounce((values: QuoteFormFields) => {
      watcher(values)
    }, 300)
  )

  useUnsafeEffect(() => {
    debouncedGeneratePreviewRef.current(form.values)

    return () => debouncedGeneratePreviewRef.current.cancel()
  }, [form.values])

  useEffect(() => {
    if (!quoteId) {
      track(trackingEvents.accounting.quotes.startCreate)
    }
  }, [quoteId])

  useUnsafeEffect(() => {
    const logoFileToFetch = form.values.salesDocumentTemplate
      ? form.values.salesDocumentTemplate?.logoFile
      : quote?.logoFile

    if (logoFileToFetch) {
      fetchLogoFile(logoFileToFetch).then((file) => {
        form.setFieldValue('logoFile', file)
      })
    } else {
      form.setFieldValue('logoFile', undefined)
    }
  }, [quote?.logoFile, form.values.salesDocumentTemplate?.logoFile])

  const title = quoteId
    ? i18n.t('finance.quotes.actions.edit')
    : i18n.t('finance.quotes.actions.create')

  if (error) {
    return (
      <Sidepanel title={title} width="extended" open onClose={onClose}>
        <NotFound />
      </Sidepanel>
    )
  }

  const handleContactSelect = (contact: ContactFragment | null) => {
    // Deal client and contact id must be the same, to avoid issues we reset the selected deal if they are different
    const dealClientId = form.values.deal?.client?.id
    const contactId = contact?.id

    if (dealClientId === contactId) {
      form.setValues({ ...form.values, contact: contact ?? undefined })
    } else {
      form.setValues({ ...form.values, contact: contact ?? undefined, deal: undefined })
    }
  }

  const handleDealSelect = (deal: FinanceDealsSelectorFragment | null) => {
    // Deal client and contact id must be the same, to avoid issues we reset the selected contact if they are different
    // If no contact is selected, we set the deal and the contact to the deal client

    const contactId = form.values.contact?.id
    const dealClientId = deal?.client?.id

    if (!contactId) {
      form.setValues({
        ...form.values,
        deal: deal ?? undefined,
        contact: deal?.client ?? undefined,
      })
    } else if (dealClientId === contactId) {
      form.setValues({ ...form.values, deal: deal ?? undefined })
    } else {
      form.setValues({ ...form.values, deal: deal ?? undefined, contact: undefined })
    }
  }

  const handleSalesDocumentTemplateSelect = (
    salesDocumentTemplate: SalesDocumentTemplateForSelectFragment | null
  ) => {
    form.setValues({
      ...form.values,
      salesDocumentTemplate: salesDocumentTemplate,
      showFooter: !!salesDocumentTemplate && salesDocumentTemplate?.footer !== '',
      footer: salesDocumentTemplate?.footer ?? undefined,
    })
  }

  const minDate = moment().subtract(180, 'days')
  const maxDate = moment().add(5, 'years')

  return (
    <Sidepanel
      title={title}
      width="extended"
      open
      onClose={onClose}
      closeLabel={i18n.t('common.close')}
      boxPadding={false}
      footer={
        <Box flexDirection="rowReverse" gap="s12">
          <Button loading={form.isSubmitting} onClick={form.submitForm} type="primary">
            {i18n.t('finance.quotes.actions.save')}
          </Button>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
        </Box>
      }
    >
      <Box flexDirection="row" height="full">
        <Box
          width="full"
          gap="s16"
          paddingTop="s16"
          overflowY="auto"
          style={{
            background: 'linear-gradient(135deg, #FAF1F1 0%, #EFE7EC 50%, #F3C4C9 100%)',
            maxHeight: '100vh',
          }}
        >
          <Box paddingX="s24">
            <Text.Normal>{i18n.t('finance.sale_invoices.form.preview')}</Text.Normal>
          </Box>
          {previewHtml && (
            <Box height="full">
              <DocumentViewer htmlContent={previewHtml} />
            </Box>
          )}
        </Box>

        <Box
          width="full"
          gap="s24"
          paddingX="s24"
          paddingTop="s24"
          paddingBottom="s96"
          flexDirection="column"
          overflowY="auto"
          style={{
            maxHeight: '100vh',
          }}
        >
          <FormikProvider value={form}>
            <Form padding="s0" notRounded>
              <Box paddingTop="s16" paddingBottom="s16" gap="s16">
                <SectionTitle
                  title={i18n.t('finance.quotes.form.title')}
                  description={i18n.t('finance.quotes.form.description')}
                />
                {salesDocumentTemplatesEnabled && (
                  <Box
                    width="full"
                    flexDirection="row"
                    gap="s16"
                    style={{
                      width: '50%',
                      paddingRight: '8px',
                    }}
                  >
                    <FinanceSalesDocumentTemplateSelector
                      label={i18n.t('finance.sale_invoices.form.template.title')}
                      emptyLabel={i18n.t('finance.sale_invoices.form.template.select_a_template')}
                      value={form.values.salesDocumentTemplate ?? null}
                      onSelect={handleSalesDocumentTemplateSelect}
                      queryParams={{
                        legalEntityIds: form.values.legalEntity?.id
                          ? [form.values.legalEntity.id]
                          : undefined,
                      }}
                      disableCreate
                      hideSearchIcon
                    />
                  </Box>
                )}
                <Box
                  width="full"
                  flexDirection="row"
                  gap="s16"
                  style={{
                    width: '50%',
                    paddingRight: '8px',
                  }}
                >
                  <TextField
                    label={i18n.t('finance.quotes.form.quote_number')}
                    value={form.values.quoteNumber || ''}
                    onChange={(value) => form.setValues({ ...form.values, quoteNumber: value })}
                    error={form.errors.quoteNumber}
                    placeholder={i18n.t('finance.quotes.form.quote_number_placeholder')}
                  />
                </Box>
                <Box flexDirection="row" gap="s16">
                  <DayPicker
                    label={i18n.t('finance.quotes.form.quote_date')}
                    value={extractDateValue(form.values.documentDate)}
                    onChange={(value) =>
                      form.setValues({ ...form.values, documentDate: dateUIToBackend(value) })
                    }
                    error={form.errors.documentDate}
                    minDate={minDate}
                    maxDate={
                      form.values.expirationDate ? moment(form.values.expirationDate) : maxDate
                    }
                  />
                  <DayPicker
                    label={i18n.t('finance.quotes.form.expiration_date')}
                    value={extractDateValue(form.values.expirationDate)}
                    onChange={(value) =>
                      form.setValues({ ...form.values, expirationDate: dateUIToBackend(value) })
                    }
                    error={form.errors.expirationDate}
                    minDate={form.values.documentDate ? moment(form.values.documentDate) : minDate}
                    maxDate={maxDate}
                  />
                </Box>
                <Box width="full" flexDirection="row" gap="s16">
                  <LegalEntitySelector
                    label={i18n.t('finance.sale_invoices.form.from_to')}
                    width="full"
                    value={form.values.legalEntity ?? null}
                    onChange={(value) =>
                      form.setValues({
                        ...form.values,
                        legalEntity: value,
                        currency: form.values.currency || value?.currency,
                        lineItems: adjustLineItemsTaxRateBasedOnCountry(
                          form.values.lineItems,
                          value?.country
                        ),
                        salesDocumentTemplate: undefined,
                      })
                    }
                    error={form.errors.legalEntity}
                    hideSearchIcon
                  />
                  <FinanceContactsSelector
                    disabled={!!defaultClient}
                    value={form.values.contact}
                    onSelect={handleContactSelect}
                    label={i18n.t('finance.sale_invoices.form.bill_to')}
                    type="finance_contact"
                    queryParams={{
                      companyId: company.id,
                      contactCreateType: 'client',
                    }}
                    error={form.errors.contact}
                    hideSearchIcon
                  />
                </Box>
                {canSeeDeals && (
                  <Box width="full" flexDirection="row" gap="s16">
                    <FinanceDealsSelector
                      disabled={!!defaultDeal}
                      value={form.values.deal}
                      onSelect={handleDealSelect}
                      label={i18n.t('finance.sale_invoices.form.deal')}
                      type="finance_deal"
                      queryParams={{
                        clientIds: form.values.contact?.id ? [form.values.contact?.id] : undefined,
                      }}
                      excludeClosedDeals
                      error={form.errors.deal}
                      hideSearchIcon
                    />
                  </Box>
                )}
                <Box
                  width="full"
                  flexDirection="row"
                  gap="s16"
                  style={{
                    width: '50%',
                    paddingRight: '8px',
                  }}
                >
                  <CurrencySelector
                    value={form.values.currency ?? ''}
                    error={form.errors.currency}
                    onChange={(value) => form.setValues({ ...form.values, currency: value })}
                  />
                </Box>
              </Box>
              <LineItemsSectionWithModal
                form={form}
                isCreditNote={false}
                readOnly={false}
                defaultTaxRate={defaultTaxRate}
                locale={access.locale}
              />
              <Box paddingTop="s16" paddingBottom="s16" gap="s16">
                <SectionTitle title={i18n.t('finance.quotes.form.discount_section')} />

                <Box
                  width="full"
                  flexDirection="row"
                  gap="s24"
                  style={{
                    width: '50%',
                    paddingRight: '8px',
                  }}
                >
                  <DiscountInput form={form} readOnly={false} />
                </Box>
              </Box>
              <Box paddingTop="s16" paddingBottom="s96">
                <CustomizeSection
                  form={form}
                  description={i18n.t('finance.quotes.form.customize_description')}
                />
              </Box>
            </Form>
          </FormikProvider>
        </Box>
      </Box>
    </Sidepanel>
  )
}

export default UpsertSidePanel
